/**
 * Os três cartões de folha da coluna direita do Fechamento.
 *
 * Papel: reproduz "Folha de Pagamento <sigla>" (l. 633–650, 3460–3516),
 * "Lançamentos de Outros Pagamentos" (l. 656–674, 4069–4100) e "Pagamentos por
 * Outros Bancos" (l. 685–702, 3426–3454): o total em destaque, uma linha com
 * percentual por item e, nos dois de folha, o botão de remover.
 *
 * Como usar: quem monta só monta o cartão quando há o que mostrar, e passa
 * `onRemover` só a quem tem `editar` — sem ele, o rodapé com o botão some. No
 * papel do Fechamento, o texto à direita do cabeçalho e o rodapé somem, como o
 * `.card-header span:last-child` e o `.no-print` da origem (l. 205, 286); cada
 * cartão evita quebrar no meio da folha.
 */
import { type FolhaImportada } from '@concierge/shared';
import { type ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';
import {
  type FolhaDoMes,
  type ResumoDosOutrosPagamentos,
  porValorDecrescente,
} from '../cartoes-do-fechamento';
import { formatarPercentual, formatarReais } from '../formato';
import { Botao } from './kit/botao';
import { CabecalhoDoCartao, Cartao, CorpoDoCartao, TituloDoCartao } from './kit/cartao';
import { LinhaComPercentual } from './kit/linha-com-percentual';
import { TotalEmDestaque } from './kit/total-em-destaque';

/** O que some no papel do Fechamento. */
const FORA_DO_PAPEL = 'print:[body[data-impressao=imprimindo-fechamento]_&]:hidden';

/**
 * O contorno comum: o cartão que não quebra, com o título e o texto da direita.
 *
 * @param titulo - O título do cartão.
 * @param aDireita - O texto de 11 px à direita do cabeçalho.
 * @param children - O corpo, com `12px 16px`.
 * @returns O cartão.
 */
function CartaoDeFolha({
  titulo,
  aDireita,
  children,
}: {
  titulo: string;
  aDireita: string;
  children: ReactNode;
}) {
  return (
    <div className="break-inside-avoid">
      <Cartao>
        <CabecalhoDoCartao>
          <TituloDoCartao>{titulo}</TituloDoCartao>
          <span className={cn('text-[11px] text-texto-fraco', FORA_DO_PAPEL)}>{aDireita}</span>
        </CabecalhoDoCartao>
        <CorpoDoCartao className="px-4 py-3">{children}</CorpoDoCartao>
      </Cartao>
    </div>
  );
}

/**
 * O rodapé com o botão de remover.
 *
 * @param rotulo - O texto do botão.
 * @param onRemover - Chamado no clique.
 * @returns O rodapé, com a borda de cima.
 */
function RodapeDeRemover({ rotulo, onRemover }: { rotulo: string; onRemover: () => void }) {
  return (
    <div className={cn('mt-2.5 border-t border-emb-borda pt-2', FORA_DO_PAPEL)}>
      <Botao variante="perigo" tamanho="sm" onClick={onRemover}>
        {rotulo}
      </Botao>
    </div>
  );
}

/**
 * "Folha de Pagamento": as folhas fechadas do mês, somadas.
 *
 * @param folha - O que `folhaDoMes` juntou.
 * @param onRemover - O "✕ Remover fechamento"; ausente para quem só vê.
 * @returns O cartão, com a linha da guia DAE que abre os subitens.
 */
export function CartaoFolhaDePagamento({
  folha,
  onRemover,
}: {
  folha: FolhaDoMes;
  onRemover?: () => void;
}) {
  return (
    <CartaoDeFolha titulo={folha.titulo} aDireita="Fora do fluxo C6">
      <TotalEmDestaque rotulo="Total do mês" valor={formatarReais(folha.total)} />
      {folha.itens.map((item, indice) => (
        <LinhaComPercentual
          // Duas folhas fechadas têm itens de mesmo nome; a posição desempata.
          key={`${item.nome}-${String(indice)}`}
          nome={item.nome}
          valor={formatarReais(item.valor)}
          percentual={formatarPercentual(item.valor, folha.total)}
          subitens={item.subitens.map((subitem) => ({
            nome: subitem.nome,
            valor: formatarReais(subitem.valor),
          }))}
        />
      ))}
      {onRemover !== undefined && (
        <RodapeDeRemover rotulo="✕ Remover fechamento" onRemover={onRemover} />
      )}
    </CartaoDeFolha>
  );
}

/**
 * "Lançamentos de Outros Pagamentos": o resumo dos lançamentos fora do C6.
 *
 * @param resumo - O que `resumoDosOutrosPagamentos` somou.
 * @returns O cartão, sem botão; a linha "Entradas" só com entrada no mês.
 */
export function CartaoOutrosPagamentos({ resumo }: { resumo: ResumoDosOutrosPagamentos }) {
  return (
    <CartaoDeFolha
      titulo="Lançamentos de Outros Pagamentos"
      aDireita="Informativo — não entra no Saldo Final"
    >
      <TotalEmDestaque rotulo="Despesas" valor={formatarReais(resumo.despesas)} />
      {resumo.porCategoria.map((linha) => (
        <LinhaComPercentual
          key={linha.categoria}
          nome={linha.categoria}
          valor={formatarReais(linha.valor)}
          percentual={formatarPercentual(linha.valor, resumo.despesas)}
        />
      ))}
      {resumo.entradas > 0 && (
        <div className="mt-2 flex justify-between border-t border-emb-borda pt-2 text-[12px]">
          <span className="text-texto-fraco">Entradas</span>
          <span className="font-bold text-sucesso">{formatarReais(resumo.entradas)}</span>
        </div>
      )}
    </CartaoDeFolha>
  );
}

/**
 * "Pagamentos por Outros Bancos": a folha XLSX do mês.
 *
 * @param folha - A folha lida da API.
 * @param siglaDoPagador - A sigla de "Saída <sigla>", da configuração da casa.
 * @param onRemover - O "✕ Remover folha"; ausente para quem só vê.
 * @returns O cartão, com os itens do maior para o menor.
 */
export function CartaoPagamentosPorOutrosBancos({
  folha,
  siglaDoPagador,
  onRemover,
}: {
  folha: FolhaImportada;
  siglaDoPagador: string;
  onRemover?: () => void;
}) {
  return (
    <CartaoDeFolha titulo="Pagamentos por Outros Bancos" aDireita={`Saída ${siglaDoPagador}`}>
      <TotalEmDestaque
        rotulo="Despesas com Pessoal (Villa Prado)"
        valor={formatarReais(folha.total)}
      />
      {porValorDecrescente(folha.itens).map((item) => (
        <LinhaComPercentual
          key={item.posicao}
          nome={item.nome}
          valor={formatarReais(item.valor)}
          percentual={formatarPercentual(item.valor, folha.total)}
        />
      ))}
      {onRemover !== undefined && (
        <RodapeDeRemover rotulo="✕ Remover folha" onRemover={onRemover} />
      )}
    </CartaoDeFolha>
  );
}
